'use client'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import { useSession, useSignIn } from '@clerk/nextjs'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { OnboardingLayout } from '@/features/onboarding'
import { emailSignInSchema, type EmailSignInFormValues } from '@/features/auth/schemas/auth.schema'
import { useFinalizeAsRole } from '@/features/auth/lib/use-finalize-as-role'
import { ROLE_HOME, roleFromSessionClaims } from '@/features/auth/lib/session'
import { AdminForgotPasswordDialog } from '@/features/auth/components/admin-forgot-password-dialog'
import { Button } from '@/components/ui/button'
import { Field, FieldDescription, FieldGroup, FieldLabel, FieldError } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { PasswordInput } from '@/components/patterns/passwordInput'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

/**
 * Staff and admin sign in with the same Clerk strategy (email + password) against the
 * same Clerk application. The tab now also enforces which role may complete sign-in from
 * it: a staff account picking the "Admin" tab (or vice versa) is signed back out immediately
 * with an error, rather than being silently redirected to its real shell — the `(staff)`/
 * `(admin)` layouts' own server-side role check remains the actual authority on access.
 */
export function TeamLoginView() {
  const router = useRouter()
  const { signIn } = useSignIn()
  const finalizeAs = useFinalizeAsRole()
  const { isLoaded: sessionLoaded, session: existingSession } = useSession()
  const [loading, setLoading] = useState(false)
  const [staffError, setStaffError] = useState<string | null>(null)
  const [adminError, setAdminError] = useState<string | null>(null)
  // Latched exactly once by the mount-only effect below, so the form is never painted for an
  // already-signed-in staff/admin even for a frame — see that effect's comment for why this
  // can't become a Server Component check instead.
  const [authCheck, setAuthCheck] = useState<'pending' | 'redirecting' | 'show-form'>('pending')

  // Redirects an already-signed-in staff/admin away from this page — but only on a genuine
  // mount (deps: `[sessionLoaded]`, evaluated once Clerk finishes loading), never as a side
  // effect of a session activating while this component stays mounted. Doing this as a
  // Server Component check on the page instead previously raced `finalizeAs` below: Clerk
  // triggers a Next.js `router.refresh()` the instant a session activates, which re-ran that
  // check mid-sign-in and redirected to the account's real dashboard before the tab-mismatch
  // check even finished — a confusing flash through the wrong dashboard and back. A
  // mount-only effect can't fire mid-transition like that.
  useEffect(() => {
    if (!sessionLoaded) return
    const role = roleFromSessionClaims(existingSession?.lastActiveToken?.jwt?.claims)
    if (role === 'staff' || role === 'admin') {
      setAuthCheck('redirecting')
      router.replace(ROLE_HOME[role])
      return
    }
    setAuthCheck('show-form')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionLoaded])

  const staffForm = useForm<EmailSignInFormValues>({
    resolver: zodResolver(emailSignInSchema),
    mode: 'onTouched',
    defaultValues: { email: '', password: '' },
  })
  const adminForm = useForm<EmailSignInFormValues>({
    resolver: zodResolver(emailSignInSchema),
    mode: 'onTouched',
    defaultValues: { email: '', password: '' },
  })

  const handleStaffSignIn = staffForm.handleSubmit(async (values) => {
    setLoading(true)
    setStaffError(null)
    try {
      const { error } = await signIn.password({
        emailAddress: values.email,
        password: values.password,
      })
      if (error) {
        setStaffError('That email or password is incorrect.')
        return
      }
      if (signIn.status === 'complete') {
        await finalizeAs('staff', setStaffError)
        return
      }
      setStaffError('Sign-in did not complete. Please try again.')
    } finally {
      setLoading(false)
    }
  })

  const handleAdminSignIn = adminForm.handleSubmit(async (values) => {
    setLoading(true)
    setAdminError(null)
    try {
      const { error } = await signIn.password({
        emailAddress: values.email,
        password: values.password,
      })
      if (error) {
        setAdminError('That email or password is incorrect.')
        return
      }
      if (signIn.status === 'complete') {
        await finalizeAs('admin', setAdminError)
        return
      }
      setAdminError('Sign-in did not complete. Please try again.')
    } finally {
      setLoading(false)
    }
  })

  // No visible loading text — this window is normally near-instant (Clerk's `useSession()`
  // resolves from local state), and a blank page is preferable here to a login form flash
  // or an explanatory message the redirect doesn't need.
  if (authCheck !== 'show-form') {
    return null
  }

  return (
    <OnboardingLayout
      title="Nivex team sign-in"
      description="For staff and administrators only."
      width="sm"
      // below={
      //   <FieldDescription className="text-center">
      //     Investor?{' '}
      //     <button
      //       type="button"
      //       onClick={() => router.push('/login')}
      //       className="text-primary font-semibold underline-offset-4 hover:underline"
      //     >
      //       Sign in here
      //     </button>
      //   </FieldDescription>
      // }
    >
      <Tabs defaultValue="staff">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="staff">Staff</TabsTrigger>
          <TabsTrigger value="admin">Admin</TabsTrigger>
        </TabsList>

        <TabsContent value="staff" className="mt-5">
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="staff-email">Staff email</FieldLabel>
              <Input
                id="staff-email"
                type="email"
                placeholder="staff@nivex.in"
                aria-invalid={!!staffForm.formState.errors.email}
                {...staffForm.register('email')}
              />
              <FieldError errors={[staffForm.formState.errors.email]} />
            </Field>
            <Field>
              <FieldLabel htmlFor="staff-password">Password</FieldLabel>
              <PasswordInput
                id="staff-password"
                placeholder="Enter your password"
                aria-invalid={!!staffForm.formState.errors.password}
                {...staffForm.register('password')}
              />
              <FieldError errors={[staffForm.formState.errors.password]} />
            </Field>
            {staffError && (
              <FieldDescription className="text-red-600">{staffError}</FieldDescription>
            )}
            <Field>
              <Button type="button" onClick={handleStaffSignIn} disabled={loading}>
                {loading ? 'Signing in…' : 'Sign In'}
              </Button>
            </Field>
          </FieldGroup>
        </TabsContent>

        <TabsContent value="admin" className="mt-5">
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="admin-email">Admin email</FieldLabel>
              <Input
                id="admin-email"
                type="email"
                placeholder="admin@nivex.in"
                aria-invalid={!!adminForm.formState.errors.email}
                {...adminForm.register('email')}
              />
              <FieldError errors={[adminForm.formState.errors.email]} />
            </Field>
            <Field>
              <div className="flex items-center justify-between">
                <FieldLabel htmlFor="admin-password">Password</FieldLabel>
                <AdminForgotPasswordDialog initialEmail={adminForm.watch('email')} />
              </div>
              <PasswordInput
                id="admin-password"
                placeholder="Enter your password"
                aria-invalid={!!adminForm.formState.errors.password}
                {...adminForm.register('password')}
              />
              <FieldError errors={[adminForm.formState.errors.password]} />
            </Field>
            {adminError && (
              <FieldDescription className="text-red-600">{adminError}</FieldDescription>
            )}
            <Field>
              <Button type="button" onClick={handleAdminSignIn} disabled={loading}>
                {loading ? 'Signing in…' : 'Sign In'}
              </Button>
            </Field>
          </FieldGroup>
        </TabsContent>
      </Tabs>
    </OnboardingLayout>
  )
}
